import * as sections from "components/Homeowner/CaseForms/ComparableGrid/Sections/Sections";
import { get } from "lodash";
import { isValidNumFieldValue, getAboveGradeUnitSubTableKeysFromGroupValue } from "components/Homeowner/CaseForms/ComparableGrid/Misc";
import { CompGridFormCodes } from "components/Homeowner/CaseForms/ComparableGrid/CompGridFormCodes";

export const calculateAdjustmentAboveGrade = (type, table, tableIndex, adjFactorThreshold) => {
  let valueToRowMappings = getAboveGradeUnitSubTableKeysFromGroupValue(type);
  let calcValue = (sub, comp, factor) => (isValidNumFieldValue(comp) && isValidNumFieldValue(sub) && isValidNumFieldValue(factor)) ? ((sub - comp) * factor).toFixed(0) : null;
  let totalFactor = adjFactorThreshold && adjFactorThreshold[type] && adjFactorThreshold[type]["Total Factor"];
  let bedroomFactor = adjFactorThreshold && adjFactorThreshold[type] && adjFactorThreshold[type]["Bedroom Factor"];
  let bathroomFactor = adjFactorThreshold && adjFactorThreshold[type] && adjFactorThreshold[type]["Bathroom Factor"];
  let halfBathroomFactor = adjFactorThreshold && adjFactorThreshold[type] && adjFactorThreshold[type]["Half Bathroom Factor"];
  let subjectTotal = table[0][valueToRowMappings.total].right.value;
  let subjectBedroom = table[0][valueToRowMappings.bedrooms].right.value;
  let subjectBathroom = table[0][valueToRowMappings.bathrooms].right.value;
  let subjectHalfBathroom = table[0][valueToRowMappings.halfBathrooms].right.value;
  const applyValueToComp = (tabIndex) => {
    let compTotal = table[tabIndex][valueToRowMappings.total].left.value;
    let compBedroom = table[tabIndex][valueToRowMappings.bedrooms].left.value;
    let compBathroom = table[tabIndex][valueToRowMappings.bathrooms].left.value;
    let compHalfBathroom = table[tabIndex][valueToRowMappings.halfBathrooms].left.value;
    table[tabIndex][valueToRowMappings.total].right.value = calcValue(subjectTotal, compTotal, totalFactor);
    table[tabIndex][valueToRowMappings.bedrooms].right.value = calcValue(subjectBedroom, compBedroom, bedroomFactor);
    table[tabIndex][valueToRowMappings.bathrooms].right.value = calcValue(subjectBathroom, compBathroom, bathroomFactor);
    table[tabIndex][valueToRowMappings.halfBathrooms].right.value = calcValue(subjectHalfBathroom, compHalfBathroom, halfBathroomFactor);
    table[tabIndex][valueToRowMappings.head].right.value = [valueToRowMappings.total, valueToRowMappings.bedrooms, valueToRowMappings.bathrooms, valueToRowMappings.halfBathrooms]
      .map(x => table[tabIndex][x].right.value)
      .reduce(((a, c) => a + +c), 0) || null;
  };
  if (tableIndex === 0) {
    table.forEach((item, index) => {
      if (index > 0) applyValueToComp(index);
    });
  } else { applyValueToComp(tableIndex); }
};

export const calculateAdjustmentGLA = (type, table, tableIndex, adjFactorThreshold) => {
  let factor = adjFactorThreshold && adjFactorThreshold[type] && adjFactorThreshold[type]["Adj Factor"];
  let threshold = adjFactorThreshold && adjFactorThreshold[type] && adjFactorThreshold[type]["Adj Threshold"];
  let subject = table[0][sections.GLA].right.value;
  if (!factor || !threshold || !subject) return;
  const applyValueToComp = (tabIndex) => {
    let comp = table[tabIndex][sections.GLA].left.value;
    if (!comp) return;
    table[tabIndex][sections.GLA].right.value = Math.abs(subject - comp) > threshold ? (((subject - comp) / threshold) * factor).toFixed(0) : null;
  };
  if (tableIndex === 0) {
    table.forEach((item, index) => {
      if (index > 0) applyValueToComp(index);
    });
  } else { applyValueToComp(tableIndex); }
};

export const calculateAdjustmentAge = (type, table, tableIndex, adjFactorThreshold) => {
  let factor = adjFactorThreshold && adjFactorThreshold[type] && adjFactorThreshold[type]["Age Adj Factor"];
  let subject = table[0][sections.ACTUAL_AGE].right.value;
  if (!factor || !subject) return;
  const applyValueToComp = (tabIndex) => {
    let comp = table[tabIndex][sections.ACTUAL_AGE].left.value;
    if (!comp) return;
    table[tabIndex][sections.ACTUAL_AGE].right.value = Math.floor(((comp - subject) * factor));
  };
  if (tableIndex === 0) {
    table.forEach((item, index) => {
      if (index > 0) applyValueToComp(index);
    });
  } else { applyValueToComp(tableIndex); }
};

export const calculateAdjustmentSite = (type, table, tableIndex, adjFactorThreshold, siteState) => {
  let threshold = adjFactorThreshold && adjFactorThreshold[type] && adjFactorThreshold[type]["Adj Threshold"];
  let factor = adjFactorThreshold && adjFactorThreshold[type] && adjFactorThreshold[type]["Adj Factor"];
  if (!threshold || !factor) return;
  let unitOfFactor = siteState["Adj Factor"] === "sf" ? "Square Feet" : "Acres";
  let subject = table[0][unitOfFactor].right.value;
  if (!subject) return;
  threshold = siteState["Adj Threshold"] === siteState["Adj Factor"] ? threshold : (unitOfFactor === "Square Feet" ? threshold * 43560 : threshold / 43560);
  const applyValueToComp = (tabIndex) => {
    let comp = table[tabIndex][unitOfFactor].left.value;
    if (!comp) return;
    table[tabIndex][sections.SITE].right.value = (((subject - comp) / threshold) * factor).toFixed(0);
  };
  if (tableIndex === 0) {
    table.forEach((item, index) => {
      if (index > 0) applyValueToComp(index);
    });
  } else { applyValueToComp(tableIndex); }
};

export const calculateAdjTotal = (table, compGridFormCode) => {

  if (![sections.NET_ADJ, sections.ADJ_NET_PRICE, sections.ADJ_GROSS_PRICE, sections.SALE_PRICE].every(x => table[0][x])) return;
  let sectionsToNotAdd = [sections.NET_ADJ, sections.ADJ_NET_PRICE, sections.ADJ_GROSS_PRICE];
  let headSections = [];
  
  const getSalePrice = tab => {
    if ([CompGridFormCodes.FNMA_APPRAISAL_COMPARABLE, CompGridFormCodes.REOMIT_APPRAISAL_COMPARABLES].includes(compGridFormCode) && tab[sections.COMPARABLE_TYPE].left.value === "list")
      return parseInt(tab[sections.FINAL_LIST_PRICE].left.value);
    return parseInt(tab[sections.SALE_PRICE].left.value);
  };
  for (let section in table[0]) {
    if (get(table[0][section], ["row", "type"]) === "head") {
      headSections.push(section);
    }
  }
  
  let fields = headSections.filter(x => !sectionsToNotAdd.includes(x));
  let netAdjustments = table.map((t, index) => {
    if (index !== 0)
      return fields
        .map(x => +t[x].right.value)
        .filter(x => !isNaN(x));
  });
  let netAdjustmentsAmount = table.map((t, index) => {
    if (index !== 0)
      return fields
        .map(x => +t[x].right.value)
        .filter(x => !isNaN(x))
        .reduce(((a, c) => a + +c), 0);
  });
  let netAdjustmentsPercentage = table.map((t, index) => {
    if (index === 0) return null;
    let salePrice = getSalePrice(t);
    if (!isNaN(netAdjustmentsAmount[index]) && salePrice) return Math.round((((netAdjustmentsAmount[index] + salePrice) / salePrice) * 100 - 100) * 100) / 100;
    else return null;
  });
  let grossAdjustmentsAmount = netAdjustmentsAmount.map((x, index) => (index !== 0) && !isNaN(x) && +(x + +getSalePrice(table[index])));
  let grossAdjustmentsPercentage = netAdjustments.map((t, index) => {
    if (index === 0) return null;
    let adjustmentTotal = t.map(x => Math.abs(x)).reduce(((a, c) => a + +c), 0);
    let salePrice = getSalePrice(table[index]);
    if (!isNaN(adjustmentTotal) && salePrice) return Math.round(((adjustmentTotal / salePrice) * 100) * 100) / 100;
    else return null;
  });
  table.forEach((item, index) => {
    if (index > 0) {
      table[index][sections.NET_ADJ].right.value = netAdjustmentsAmount[index];
      table[index][sections.ADJ_NET_PRICE].left.value = netAdjustmentsPercentage[index];
      table[index][sections.ADJ_NET_PRICE].right.value = grossAdjustmentsAmount[index];
      table[index][sections.ADJ_GROSS_PRICE].left.value = grossAdjustmentsPercentage[index];
    }
  });
};

export const updateAdjustment = (table, index, subTableKey, direction) => {
  const addAdjSubSections = (table, index, subTableKeys, direction) => {
    return subTableKeys.filter(x => table[index][x]).map(x => table[index][x][direction].value).reduce(((a, c) => a + +c), 0);
  };
  if ([sections.LOCATION_RATING, sections.LOCATION_TYPE, sections.LOCATION_TYPE_2, sections.LOCATION_TYPE_1_OTHER, sections.LOCATION_TYPE_2_OTHER].includes(subTableKey)) {
    table[index][sections.LOCATION][direction].value = addAdjSubSections(table, index, [sections.LOCATION_RATING, sections.LOCATION_TYPE, sections.LOCATION_TYPE_2, sections.LOCATION_TYPE_1_OTHER, sections.LOCATION_TYPE_2_OTHER], direction);
  } else if ([sections.VIEW_RATING, sections.VIEW_TYPE].includes(subTableKey)) {
    table[index][sections.VIEW][direction].value = addAdjSubSections(table, index, [sections.VIEW_RATING, sections.VIEW_TYPE], direction);
  } else if ([sections.HEATING_1004P, sections.COOLING_1004P].includes(subTableKey)) {
    table[index][sections.HEATING_COOLING][direction].value = addAdjSubSections(table, index, [sections.HEATING_1004P, sections.COOLING_1004P], direction);
  } else if ([sections.DRIVEWAY, sections.NUM_OF_CARS, sections.CAR_STORAGE_1, sections.NUM_OF_CARS_1, sections.ATTACHMENT_TYPE_1, sections.CAR_STORAGE_2, sections.NUM_OF_CARS_2, sections.ATTACHMENT_TYPE_2].includes(subTableKey)) {
    table[index][sections.GARAGE_CARPORT][direction].value = addAdjSubSections(table, index, [sections.DRIVEWAY, sections.NUM_OF_CARS, sections.CAR_STORAGE_1, sections.NUM_OF_CARS_1, 
    sections.ATTACHMENT_TYPE_1, sections.CAR_STORAGE_2, sections.NUM_OF_CARS_2, sections.ATTACHMENT_TYPE_2], direction);
  } else if ([sections.DRIVEWAY_ENABLED, sections.NUM_OF_CARS_MVALUE, sections.CAR_STORAGE_1_MVALUE, sections.NUM_OF_CARS_1_MVALUE, sections.ATTACHMENT_TYPE_1_MVALUE, sections.CAR_STORAGE_2_MVALUE, sections.NUM_OF_CARS_2_MVALUE, sections.ATTACHMENT_TYPE_2_MVALUE].includes(subTableKey)) {
    table[index][sections.GARAGE_CARPORT_MVALUE][direction].value = addAdjSubSections(table, index, [sections.DRIVEWAY_ENABLED, sections.NUM_OF_CARS_MVALUE, sections.CAR_STORAGE_1_MVALUE, sections.NUM_OF_CARS_1_MVALUE, 
    sections.ATTACHMENT_TYPE_1_MVALUE, sections.CAR_STORAGE_2_MVALUE, sections.NUM_OF_CARS_2_MVALUE, sections.ATTACHMENT_TYPE_2_MVALUE], direction);
  } else if ([sections.BELOW_GRADE_TOT_SF, sections.BELOW_GRADE_FIN_SF, sections.BASEMENT_EXIT_TYPE].includes(subTableKey)) {
    table[index][sections.BASEMENT_FINISHED][direction].value = addAdjSubSections(table, index, [sections.BELOW_GRADE_TOT_SF, sections.BELOW_GRADE_FIN_SF, sections.BASEMENT_EXIT_TYPE], direction);
  } else if ([sections.SALE_DATE_1004P, sections.CONTRACT_DATE].includes(subTableKey)) {
    table[index][sections.DATE_OF_SALE_TIME][direction].value = addAdjSubSections(table, index, [sections.SALE_DATE_1004P, sections.CONTRACT_DATE], direction);
  } else if ([sections.TOTAL, sections.BEDROOMS, sections.BATHROOMS, sections.HALF_BATHROOMS].includes(subTableKey)) {
    table[index][sections.ABOVE_GRADE_ROOM_COUNT].right.value = addAdjSubSections(table, index, [sections.TOTAL, sections.BEDROOMS, sections.BATHROOMS, sections.HALF_BATHROOMS], direction);
  } else if ([sections.REC, sections.BED, sections.FULL_BATH, "Half Bath", sections.OTHER].includes(subTableKey)) {
    table[index][sections.BELOW_GRADE].right.value = addAdjSubSections(table, index, [sections.REC, sections.BED, sections.FULL_BATH, "Half Bath", sections.OTHER], direction);
  } else if ([sections.TOTAL_UNIT_1, sections.BEDROOMS_UNIT_1, sections.BATHROOMS_UNIT_1, sections.HALF_BATHROOMS_UNIT_1].includes(subTableKey)) {
    table[index][sections.UNIT_1].right.value = addAdjSubSections(table, index, [sections.TOTAL_UNIT_1, sections.BEDROOMS_UNIT_1, sections.BATHROOMS_UNIT_1, sections.HALF_BATHROOMS_UNIT_1], direction);
  } else if ([sections.TOTAL_UNIT_2, sections.BEDROOMS_UNIT_2, sections.BATHROOMS_UNIT_2, sections.HALF_BATHROOMS_UNIT_2].includes(subTableKey)) {
    table[index][sections.UNIT_2].right.value = addAdjSubSections(table, index, [sections.TOTAL_UNIT_2, sections.BEDROOMS_UNIT_2, sections.BATHROOMS_UNIT_2, sections.HALF_BATHROOMS_UNIT_2], direction);
  } else if ([sections.TOTAL_UNIT_3, sections.BEDROOMS_UNIT_3, sections.BATHROOMS_UNIT_3, sections.HALF_BATHROOMS_UNIT_3].includes(subTableKey)) {
    table[index][sections.UNIT_3].right.value = addAdjSubSections(table, index, [sections.TOTAL_UNIT_3, sections.BEDROOMS_UNIT_3, sections.BATHROOMS_UNIT_3, sections.HALF_BATHROOMS_UNIT_3], direction);
  } else if ([sections.TOTAL_UNIT_4, sections.BEDROOMS_UNIT_4, sections.BATHROOMS_UNIT_4, sections.HALF_BATHROOMS_UNIT_4].includes(subTableKey)) {
    table[index][sections.UNIT_4].right.value = addAdjSubSections(table, index, [sections.TOTAL_UNIT_4, sections.BEDROOMS_UNIT_4, sections.BATHROOMS_UNIT_4, sections.HALF_BATHROOMS_UNIT_4], direction);
  }
};